import React, { useState, useEffect, useRef } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const TeacherAttendance = ({ onNavigate, userInfo }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Photo capture states
  const [showCamera, setShowCamera] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [photoUploaded, setPhotoUploaded] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // AI Head Counter states
  const [faceApiLoaded, setFaceApiLoaded] = useState(false);
  const [analyzingPhoto, setAnalyzingPhoto] = useState(false);
  const [faceCount, setFaceCount] = useState(0);
  const [countMatched, setCountMatched] = useState(false);
  const [showCountComparison, setShowCountComparison] = useState(false);

  // Consent and image quality states
  const [consentChecked, setConsentChecked] = useState(false);
  const [isImageBlurry, setIsImageBlurry] = useState(false);
  const [blurryConsentChecked, setBlurryConsentChecked] = useState(false);

  // ============================================
  // ATTENDANCE TYPE STATE
  // ============================================
  const [attendanceType, setAttendanceType] = useState('regular');

  // ============================================
  // REGULAR ATTENDANCE STATES
  // ============================================
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [departments, setDepartments] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [batches, setBatches] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);

  // ============================================
  // TRAINING ATTENDANCE STATES
  // ============================================
  const [selectedTrainingProgram, setSelectedTrainingProgram] = useState('');
  const [selectedTrainingBatch, setSelectedTrainingBatch] = useState('');
  const [selectedTrainingSubject, setSelectedTrainingSubject] = useState('');
  const [trainingPrograms, setTrainingPrograms] = useState([]);
  const [trainingBatches, setTrainingBatches] = useState([]);
  const [trainingSubjects, setTrainingSubjects] = useState([]);
  const [trainingStudents, setTrainingStudents] = useState([]);

  // ============================================
  // SHARED STATES
  // ============================================
  const [teacherData, setTeacherData] = useState({});
  const [attendanceData, setAttendanceData] = useState({});
  const [showStudentList, setShowStudentList] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Notification state
  const [notifications, setNotifications] = useState([]);

  // ============================================
  // AI FACE DETECTION INITIALIZATION
  // ============================================
  useEffect(() => {
    const initializeAI = async () => {
      try {
        // Load the AI Face Detection module
        if (!window.AIFaceDetector) {
          console.log('Loading AI Face Detection module...');

          // Create and append the aiface.js script
          const script = document.createElement('script');
          script.innerHTML = `
            // aiface.js - AI Face Detection Module
            class AIFaceDetector {
              constructor() {
                this.model = null;
                this.isLoaded = false;
                this.isLoading = false;
              }

              async initialize() {
                if (this.isLoaded) return true;
                if (this.isLoading) return false;

                this.isLoading = true;
                
                try {
                  console.log('Loading TensorFlow.js and BlazeFace model...');
                  
                  await this.loadScript('https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@3.21.0/dist/tf.min.js');
                  await this.loadScript('https://cdn.jsdelivr.net/npm/@tensorflow-models/blazeface@0.0.7/dist/blazeface.min.js');
                  
                  if (typeof tf === 'undefined') {
                    throw new Error('TensorFlow.js failed to load');
                  }

                  console.log('Loading BlazeFace model...');
                  this.model = await blazeface.load();
                  
                  this.isLoaded = true;
                  this.isLoading = false;
                  
                  console.log('AI Face Detection ready!');
                  return true;
                  
                } catch (error) {
                  console.error('Failed to initialize AI Face Detector:', error);
                  this.isLoading = false;
                  this.isLoaded = false;
                  return false;
                }
              }

              loadScript(src) {
                return new Promise((resolve, reject) => {
                  const existingScript = document.querySelector(\`script[src="\${src}"]\`);
                  if (existingScript) {
                    resolve();
                    return;
                  }

                  const script = document.createElement('script');
                  script.src = src;
                  script.onload = () => resolve();
                  script.onerror = () => reject(new Error(\`Failed to load script: \${src}\`));
                  
                  setTimeout(() => reject(new Error('Script loading timeout')), 20000);
                  
                  document.head.appendChild(script);
                });
              }

              async detectFaces(imageElement) {
                if (!this.isLoaded || !this.model) {
                  throw new Error('AI model not loaded');
                }

                try {
                  if (!imageElement.complete || imageElement.naturalWidth === 0) {
                    throw new Error('Image not loaded properly');
                  }

                  console.log(\`Analyzing image: \${imageElement.naturalWidth}x\${imageElement.naturalHeight}\`);
                  
                  const predictions = await this.model.estimateFaces(imageElement, false);
                  
                  console.log(\`Detected \${predictions.length} faces\`);
                  
                  const validFaces = predictions.filter(prediction => {
                    return prediction.landmarks && prediction.landmarks.length > 0;
                  });

                  return {
                    count: validFaces.length,
                    faces: validFaces,
                    success: true
                  };

                } catch (error) {
                  console.error('Face detection error:', error);
                  throw error;
                }
              }

              async detectFacesEnhanced(imageElement, options = {}) {
                const defaultOptions = {
                  minFaceSize: 15,
                  maxFaceSize: 800
                };

                const config = { ...defaultOptions, ...options };

                try {
                  const result = await this.detectFaces(imageElement);
                  
                  if (!result.success) {
                    return result;
                  }

                  const filteredFaces = result.faces.filter(face => {
                    const faceWidth = face.bottomRight[0] - face.topLeft[0];
                    const faceHeight = face.bottomRight[1] - face.topLeft[1];
                    const faceSize = Math.max(faceWidth, faceHeight);
                    
                    return faceSize >= config.minFaceSize && faceSize <= config.maxFaceSize;
                  });

                  return {
                    count: filteredFaces.length,
                    faces: filteredFaces,
                    success: true,
                    rawCount: result.count,
                    filtered: result.count - filteredFaces.length
                  };

                } catch (error) {
                  return {
                    count: 0,
                    faces: [],
                    success: false,
                    error: error.message
                  };
                }
              }

              async analyzePhotoBlob(photoBlob) {
                return new Promise((resolve) => {
                  const img = new Image();
                  const imageUrl = URL.createObjectURL(photoBlob);

                  img.onload = async () => {
                    try {
                      const result = await this.detectFacesEnhanced(img);
                      URL.revokeObjectURL(imageUrl);
                      resolve(result);
                    } catch (error) {
                      URL.revokeObjectURL(imageUrl);
                      resolve({
                        count: 0,
                        faces: [],
                        success: false,
                        error: error.message
                      });
                    }
                  };

                  img.onerror = () => {
                    URL.revokeObjectURL(imageUrl);
                    resolve({
                      count: 0,
                      faces: [],
                      success: false,
                      error: 'Failed to load image'
                    });
                  };

                  setTimeout(() => {
                    if (!img.complete) {
                      URL.revokeObjectURL(imageUrl);
                      resolve({
                        count: 0,
                        faces: [],
                        success: false,
                        error: 'Image loading timeout'
                      });
                    }
                  }, 10000);

                  img.src = imageUrl;
                });
              }

              getStatus() {
                return {
                  isLoaded: this.isLoaded,
                  isLoading: this.isLoading,
                  modelType: 'BlazeFace',
                  ready: this.isLoaded && this.model !== null
                };
              }

              cleanup() {
                if (this.model) {
                  this.model = null;
                }
                this.isLoaded = false;
                this.isLoading = false;
              }
            }

            window.AIFaceDetector = AIFaceDetector;
            window.aiDetector = new AIFaceDetector();
          `;

          document.head.appendChild(script);
        }

        // Initialize the AI detector
        if (window.aiDetector) {
          console.log('Initializing AI Face Detector...');
          setFaceApiLoaded(false); // Set loading state

          const initialized = await window.aiDetector.initialize();

          if (initialized) {
            setFaceApiLoaded(true);
            showNotification('AI Head Counter ready! Photos will be automatically analyzed.', 'success');
          } else {
            setFaceApiLoaded(false);
            showNotification('AI Head Counter failed to load. Manual verification mode active.', 'warning');
          }
        }

      } catch (error) {
        console.error('Error initializing AI Face Detector:', error);
        setFaceApiLoaded(false);
        showNotification('AI Head Counter unavailable. Using manual verification mode.', 'info');
      }
    };

    // Initialize AI after component mounts
    const timer = setTimeout(initializeAI, 1500);
    return () => clearTimeout(timer);
  }, []);

  // ============================================
  // EXISTING USEEFFECTS
  // ============================================
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Check photo status when filters change
  useEffect(() => {
    if (showStudentList) {
      checkPhotoStatus();
    }
  }, [showStudentList, selectedDepartment, selectedYear, selectedBatch, selectedSubject, selectedTrainingProgram, selectedTrainingBatch, selectedTrainingSubject]);

  // ============================================
  // AI FACE DETECTION FUNCTIONS
  // ============================================
  const detectFacesInPhoto = async (imageElement) => {
    if (!faceApiLoaded || !window.faceapi) {
      throw new Error('Face detection library not loaded');
    }

    try {
      // Ensure image is loaded
      if (imageElement.naturalWidth === 0) {
        throw new Error('Image not loaded properly');
      }

      console.log('Starting face detection...');

      const detections = await window.faceapi.detectAllFaces(
        imageElement,
        new window.faceapi.TinyFaceDetectorOptions({
          inputSize: 416, // Reduced from 512 for better performance
          scoreThreshold: 0.4 // Lowered threshold for better detection
        })
      );

      console.log(`Detected ${detections.length} faces`);
      return detections.length;
    } catch (error) {
      console.error('Error detecting faces:', error);
      throw error;
    }
  };

  const analyzePhotoForHeadCount = async (photoBlob) => {
    setAnalyzingPhoto(true);
    setShowCountComparison(true);

    try {
      // Get expected student count for comparison
      const currentStudents = attendanceType === 'regular' ? students : trainingStudents;
      const presentCount = currentStudents.filter(student =>
        attendanceData[student.student_id]?.status === 'present'
      ).length;

      // Check if AI is available
      if (!faceApiLoaded || !window.aiDetector || !window.aiDetector.getStatus().ready) {
        console.log('AI not available, using manual verification');
        setFaceCount(0);
        setCountMatched(true);
        showNotification(`Photo captured! ${presentCount} students are marked present. Please verify the photo manually before uploading.`, 'info');
        return true;
      }

      // Use AI to analyze the photo
      console.log('Starting AI photo analysis...');
      const result = await window.aiDetector.analyzePhotoBlob(photoBlob);

      if (!result.success) {
        console.error('AI analysis failed:', result.error);
        setFaceCount(0);
        setCountMatched(true);
        showNotification(`AI analysis failed (${result.error}). Proceeding with manual verification for ${presentCount} students.`, 'warning');
        return true;
      }

      // Process AI results
      const detectedFaces = result.count;
      setFaceCount(detectedFaces);

      // Check if counts match (with some tolerance)
      const tolerance = 1; // Allow 1 face difference due to angle/lighting
      const difference = Math.abs(detectedFaces - presentCount);
      const matched = difference <= tolerance;

      setCountMatched(matched);

      // Provide detailed feedback
      if (detectedFaces === presentCount) {
        showNotification(`Perfect match! AI detected ${detectedFaces} faces matching ${presentCount} present students.`, 'success');
      } else if (difference === 1) {
        showNotification(`Close match: AI detected ${detectedFaces} faces, ${presentCount} students present. Small difference is acceptable (lighting/angle). You can proceed.`, 'warning');
      } else if (detectedFaces < presentCount) {
        showNotification(`Fewer faces detected: AI found ${detectedFaces} faces but ${presentCount} students are present. Some students may not be visible or facing camera.`, 'error');
      } else {
        showNotification(`More faces detected: AI found ${detectedFaces} faces but only ${presentCount} students are present. Extra faces may be visitors or detection errors.`, 'error');
      }

      // Additional information for debugging
      if (result.filtered > 0) {
        console.log(`Filtered out ${result.filtered} detections (size/quality filtering)`);
      }

      return matched;

    } catch (error) {
      console.error('Photo analysis error:', error);

      // Get fallback count for manual verification
      const currentStudents = attendanceType === 'regular' ? students : trainingStudents;
      const presentCount = currentStudents.filter(student =>
        attendanceData[student.student_id]?.status === 'present'
      ).length;

      setFaceCount(0);
      setCountMatched(true);
      showNotification(`Analysis error occurred. Proceeding with manual verification for ${presentCount} students.`, 'warning');
      return true;

    } finally {
      setAnalyzingPhoto(false);
    }
  };

  // ============================================
  // PHOTO CAPTURE FUNCTIONS (UPDATED)
  // ============================================
  const checkPhotoStatus = async () => {
    try {
      const params = new URLSearchParams({
        attendanceType,
        date: new Date().toISOString().split('T')[0]
      });

      if (attendanceType === 'regular') {
        params.append('department', selectedDepartment);
        params.append('year', selectedYear);
        params.append('batch_name', selectedBatch);
        params.append('subject', selectedSubject);
      } else {
        params.append('training_program_id', selectedTrainingProgram);
        params.append('training_batch_id', selectedTrainingBatch);
        params.append('subject_code', selectedTrainingSubject);
      }

      const response = await apiCall(`/attendance/photo-status?${params}`);

      if (response.success && response.data.photoExists) {
        setPhotoUploaded(true);
        setCapturedPhoto(response.data.photoPath);
        showNotification('✅ Classroom photo already captured for today', 'success');
      } else {
        setPhotoUploaded(false);
        setCapturedPhoto(null);
      }
    } catch (error) {
      console.error('Error checking photo status:', error);
    }
  };

  const startCamera = async () => {
    try {
      setShowCamera(true);

      // Stop any existing stream first
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      const constraints = {
        video: {
          facingMode: 'environment', // Use back camera on mobile
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 }
        },
        audio: false
      };

      console.log('📷 Requesting camera access...');
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      streamRef.current = stream;

      // Wait for video element to be available
      setTimeout(() => {
        if (videoRef.current && stream) {
          videoRef.current.srcObject = stream;

          // Add event listeners
          videoRef.current.onloadedmetadata = () => {
            console.log('📹 Video metadata loaded');
            videoRef.current.play().catch(error => {
              console.error('Error playing video:', error);
            });
          };

          videoRef.current.oncanplay = () => {
            console.log('📹 Video can play');
          };

          videoRef.current.onerror = (error) => {
            console.error('Video error:', error);
            showNotification('Camera video error occurred', 'error');
          };
        }
      }, 100);

      showNotification('📷 Camera started successfully!', 'success');

    } catch (error) {
      console.error('Error accessing camera:', error);

      let errorMessage = 'Failed to access camera. ';

      if (error.name === 'NotAllowedError') {
        errorMessage += 'Please allow camera permission and try again.';
      } else if (error.name === 'NotFoundError') {
        errorMessage += 'No camera device found.';
      } else if (error.name === 'NotSupportedError') {
        errorMessage += 'Camera not supported by this browser.';
      } else {
        errorMessage += error.message;
      }

      showNotification(errorMessage, 'error');
      setShowCamera(false);
    }
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) {
      showNotification('Camera not ready. Please try again.', 'error');
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Check if video is playing
    if (video.readyState !== video.HAVE_ENOUGH_DATA) {
      showNotification('Camera not ready. Please wait a moment and try again.', 'error');
      return;
    }

    try {
      const context = canvas.getContext('2d');

      // Set canvas dimensions to video dimensions
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;

      console.log('📸 Capturing photo:', canvas.width, 'x', canvas.height);

      // Draw the video frame to the canvas
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to blob with higher quality
      canvas.toBlob(async (blob) => {
        if (blob) {
          setCapturedPhoto(blob);
          stopCamera();
          setConsentChecked(false);
          setBlurryConsentChecked(false);
          showNotification('📸 Photo captured! Analyzing image quality and head count...', 'info');

          // Simple blur detection
          setTimeout(() => {
            const img = new Image();
            img.onload = () => {
              const tempCanvas = document.createElement('canvas');
              const ctx = tempCanvas.getContext('2d');
              tempCanvas.width = Math.min(img.width, 400);
              tempCanvas.height = Math.min(img.height, 400);
              ctx.drawImage(img, 0, 0, tempCanvas.width, tempCanvas.height);
              const imageData = ctx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
              const data = imageData.data;
              let edgeSum = 0;
              for (let i = 0; i < data.length; i += 4) {
                const gray = (data[i] + data[i + 1] + data[i + 2]) / 3;
                if (i > 4) edgeSum += Math.abs(gray - (data[i - 4] + data[i - 3] + data[i - 2]) / 3);
              }
              const avgEdge = edgeSum / (data.length / 4);
              const isBlurry = avgEdge < 10;
              setIsImageBlurry(isBlurry);
              if (isBlurry) showNotification('⚠️ Image appears blurry! Consent required to proceed.', 'warning');
            };
            img.src = URL.createObjectURL(blob);
          }, 100);

          // Analyze photo for head count if face API is loaded
          if (faceApiLoaded) {
            await analyzePhotoForHeadCount(blob);
          } else {
            showNotification('📸 Photo captured successfully! (AI analysis not available)', 'success');
            setCountMatched(true); // Allow manual override
          }
        } else {
          showNotification('Failed to capture photo. Please try again.', 'error');
        }
      }, 'image/jpeg', 0.9);

    } catch (error) {
      console.error('Error capturing photo:', error);
      showNotification('Error capturing photo: ' + error.message, 'error');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log('🔴 Stopped camera track:', track.kind);
      });
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setShowCamera(false);
    console.log('📷 Camera stopped');
  };

  const uploadPhoto = async () => {
    if (!capturedPhoto) {
      showNotification('Please capture a photo first', 'error');
      return;
    }

    // Check AI verification results if available
    if (faceApiLoaded && showCountComparison && !countMatched) {
      const currentStudents = attendanceType === 'regular' ? students : trainingStudents;
      const presentCount = currentStudents.filter(student =>
        attendanceData[student.student_id]?.status === 'present'
      ).length;

      const difference = Math.abs(faceCount - presentCount);

      if (difference > 2) {
        showNotification(`Head count verification failed! AI detected ${faceCount} faces but ${presentCount} students are present. Please verify attendance and retake photo, or use Force Upload if you're certain.`, 'error');
        return;
      }
    }

    setUploadingPhoto(true);

    try {
      const formData = new FormData();

      // Convert blob to file
      const photoFile = new File([capturedPhoto], `classroom_${Date.now()}.jpg`, {
        type: 'image/jpeg'
      });
      formData.append('classroomPhoto', photoFile);

      // Add attendance context
      formData.append('attendanceType', attendanceType);
      formData.append('date', new Date().toISOString().split('T')[0]);

      // Add AI verification data
      const currentStudents = attendanceType === 'regular' ? students : trainingStudents;
      const presentCount = currentStudents.filter(student =>
        attendanceData[student.student_id]?.status === 'present'
      ).length;

      formData.append('expectedStudents', presentCount.toString());
      formData.append('aiEnabled', faceApiLoaded.toString());

      if (faceApiLoaded && showCountComparison) {
        formData.append('detectedFaces', faceCount.toString());
        formData.append('countMatched', countMatched.toString());
        formData.append('verificationMode', 'ai');
      } else {
        formData.append('verificationMode', 'manual');
      }

      if (attendanceType === 'regular') {
        formData.append('department', selectedDepartment);
        formData.append('year', selectedYear);
        formData.append('batch_name', selectedBatch);
        formData.append('subject', selectedSubject);
      } else {
        formData.append('training_program_id', selectedTrainingProgram);
        formData.append('training_batch_id', selectedTrainingBatch);
        formData.append('subject_code', selectedTrainingSubject);
      }

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      console.log('Uploading photo with AI verification data...');

      const response = await fetch(`${API_LINK}/api/teacher/attendance/upload-photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        setPhotoUploaded(true);
        if (faceApiLoaded && showCountComparison) {
          showNotification(`Classroom photo uploaded with AI verification! Detected ${faceCount} faces for ${presentCount} students.`, 'success');
        } else {
          showNotification('Classroom photo uploaded successfully! You can now submit attendance.', 'success');
        }
      } else {
        throw new Error(result.message || 'Failed to upload photo');
      }

    } catch (error) {
      console.error('Error uploading photo:', error);
      showNotification('Failed to upload photo: ' + error.message, 'error');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const retakePhoto = () => {
    setCapturedPhoto(null);
    setPhotoUploaded(false);
    setFaceCount(0);
    setCountMatched(false);
    setShowCountComparison(false);
    setConsentChecked(false);
    setIsImageBlurry(false);
    setBlurryConsentChecked(false);
    startCamera();
  };

  const forceUpload = async () => {
    if (!capturedPhoto) {
      showNotification('Please capture a photo first', 'error');
      return;
    }

    // Check if consent is provided
    const needsCountConsent = showCountComparison && !countMatched;
    const needsBlurConsent = isImageBlurry;

    if (needsCountConsent && !consentChecked) {
      showNotification('Please check the consent box to acknowledge the attendance discrepancy', 'error');
      return;
    }

    if (needsBlurConsent && !blurryConsentChecked) {
      showNotification('Please check the consent box to acknowledge the blurry image', 'error');
      return;
    }

    // Force upload with consent
    setCountMatched(true);
    await uploadPhoto();
  };

  // ============================================
  // ATTENDANCE TYPE CHANGE HANDLER
  // ============================================
  const handleAttendanceTypeChange = (type) => {
    setAttendanceType(type);
    resetAllStates();

    if (type === 'regular') {
      fetchInitialData();
    } else if (type === 'training') {
      fetchTrainingData();
    }
  };

  const resetAllStates = () => {
    setSelectedDepartment('');
    setSelectedYear('');
    setSelectedBatch('');
    setSelectedSubject('');
    setAvailableYears([]);
    setBatches([]);
    setSubjects([]);
    setStudents([]);

    setSelectedTrainingProgram('');
    setSelectedTrainingBatch('');
    setSelectedTrainingSubject('');
    setTrainingBatches([]);
    setTrainingSubjects([]);
    setTrainingStudents([]);

    setShowStudentList(false);
    setAttendanceData({});

    // Reset photo states
    setCapturedPhoto(null);
    setPhotoUploaded(false);
    setShowCamera(false);
    setFaceCount(0);
    setCountMatched(false);
    setShowCountComparison(false);
    stopCamera();
  };

  // ============================================
  // API HELPER FUNCTION
  // ============================================
  const apiCall = async (endpoint, options = {}) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication token not found');
    }

    const response = await fetch(`${API_LINK}/api/teacher${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      if (response.status === 401) {
        handleLogout();
        throw new Error('Session expired');
      }

      // Better error handling
      const errorData = await response.text();
      console.error('API Error:', response.status, errorData);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorData}`);
    }

    return response.json();
  };

  // ============================================
  // DATA FETCHING FUNCTIONS
  // ============================================
  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [teacherInfo, departmentsData] = await Promise.all([
        apiCall('/profile'),
        apiCall('/attendance/departments')
      ]);

      if (teacherInfo.success) {
        setTeacherData(teacherInfo.data);
      }

      if (departmentsData.success) {
        setDepartments(departmentsData.data);
      }

    } catch (error) {
      console.error('Error fetching initial data:', error);
      setError('Failed to load initial data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainingData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [teacherInfo, programsData] = await Promise.all([
        apiCall('/profile'),
        apiCall('/training/programs')
      ]);

      if (teacherInfo.success) {
        setTeacherData(teacherInfo.data);
      }

      if (programsData.success) {
        setTrainingPrograms(programsData.data);
      }

    } catch (error) {
      console.error('Error fetching training data:', error);
      setError('Failed to load training data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableYears = async (department) => {
    try {
      const response = await apiCall(`/attendance/years?department=${encodeURIComponent(department)}`);

      if (response.success) {
        setAvailableYears(response.data);
      } else {
        setAvailableYears([]);
      }
    } catch (error) {
      console.error('Error fetching years:', error);
      setAvailableYears([]);
      showNotification('Failed to fetch years', 'error');
    }
  };

  const fetchBatches = async (department, year) => {
    try {
      const response = await apiCall(`/attendance/batches?department=${encodeURIComponent(department)}&year=${year}`);

      if (response.success) {
        setBatches(response.data);
      } else {
        setBatches([]);
      }
    } catch (error) {
      console.error('Error fetching batches:', error);
      setBatches([]);
      showNotification('Failed to fetch batches', 'error');
    }
  };

  const fetchSubjects = async (department, year) => {
    try {
      const response = await apiCall(`/attendance/subjects?department=${encodeURIComponent(department)}&year=${year}`);

      if (response.success) {
        setSubjects(response.data);
      } else {
        setSubjects([]);
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      setSubjects([]);
      showNotification('Failed to fetch subjects', 'error');
    }
  };

  const fetchStudents = async (batch, subject) => {
    try {
      setLoading(true);

      const response = await apiCall(`/attendance/students?batch=${encodeURIComponent(batch)}&subject=${subject}`);

      if (response.success) {
        setStudents(response.data);

        const initialAttendance = {};
        response.data.forEach(student => {
          // Determine initial status based on internship attendance
          let initialStatus = 'present';
          if (student.is_in_internship) {
            if (student.internship_attendance_today === 'REJECTED' ||
              student.internship_attendance_today === 'NO_SUBMISSION') {
              initialStatus = 'absent';
            } else {
              initialStatus = 'present'; // APPROVED or PENDING
            }
          }

          initialAttendance[student.student_id] = {
            status: initialStatus,
            notificationStatus: 'pending',
            isInternship: !!student.is_in_internship,
            internshipStatus: student.internship_attendance_today,
            internshipDetails: student.is_in_internship ? {
              company: student.company_name,
              endDate: student.internship_end_date,
              status: student.internship_attendance_today
            } : null
          };
        });

        setAttendanceData(initialAttendance);
        setShowStudentList(true);

        // Show detailed internship breakdown
        if (response.meta && response.meta.internship_stats) {
          const stats = response.meta.internship_stats;
          if (stats.total > 0) {
            const messages = [];
            if (stats.approved > 0) messages.push(`${stats.approved} approved (present)`);
            if (stats.rejected > 0) messages.push(`${stats.rejected} rejected (absent)`);
            if (stats.pending > 0) messages.push(`${stats.pending} pending (present)`);
            if (stats.noSubmission > 0) messages.push(`${stats.noSubmission} no submission (absent)`);

            showNotification(
              `📊 Internship students: ${messages.join(', ')}`,
              'info'
            );
          }
        }
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      showNotification('Failed to fetch students', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // TRAINING FUNCTIONS
  // ============================================
  const fetchTrainingBatches = async (programId) => {
    try {
      const response = await apiCall(`/training/batches?program_id=${programId}`);
      if (response.success) {
        setTrainingBatches(response.data);
      }
    } catch (error) {
      console.error('Error fetching training batches:', error);
      showNotification('Failed to fetch training batches', 'error');
    }
  };

  const fetchTrainingSubjects = async (programId) => {
    try {
      const response = await apiCall(`/training/subjects?program_id=${programId}`);
      if (response.success) {
        setTrainingSubjects(response.data);
      }
    } catch (error) {
      console.error('Error fetching training subjects:', error);
      showNotification('Failed to fetch training subjects', 'error');
    }
  };

  const fetchTrainingStudents = async (trainingBatchId, programId) => {
    try {
      setLoading(true);
      const response = await apiCall(`/training/students?training_batch_id=${trainingBatchId}&program_id=${programId}`);

      if (response.success) {
        setTrainingStudents(response.data);

        const initialAttendance = {};
        response.data.forEach(student => {
          initialAttendance[student.student_id] = {
            status: 'present',
            notificationStatus: 'pending'
          };
        });
        setAttendanceData(initialAttendance);
        setShowStudentList(true);
      }
    } catch (error) {
      console.error('Error fetching training students:', error);
      showNotification('Failed to fetch training students', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // CHANGE HANDLERS
  // ============================================
  const handleDepartmentChange = (value) => {
    setSelectedDepartment(value);
    setSelectedYear('');
    setSelectedBatch('');
    setSelectedSubject('');
    setAvailableYears([]);
    setBatches([]);
    setSubjects([]);
    setShowStudentList(false);
    resetPhotoStates();

    if (value) {
      fetchAvailableYears(value);
    }
  };

  const handleYearChange = (value) => {
    setSelectedYear(value);
    setSelectedBatch('');
    setSelectedSubject('');
    setSubjects([]);
    setShowStudentList(false);
    resetPhotoStates();

    if (value && selectedDepartment) {
      fetchBatches(selectedDepartment, value);
      fetchSubjects(selectedDepartment, value);
    }
  };

  const handleBatchChange = (value) => {
    setSelectedBatch(value);
    setSelectedSubject('');
    setShowStudentList(false);
    resetPhotoStates();
  };

  const handleSubjectChange = (value) => {
    setSelectedSubject(value);
    resetPhotoStates();
    if (value && selectedBatch) {
      fetchStudents(selectedBatch, value);
    } else {
      setShowStudentList(false);
    }
  };

  const handleTrainingProgramChange = (value) => {
    setSelectedTrainingProgram(value);
    setSelectedTrainingBatch('');
    setSelectedTrainingSubject('');
    setTrainingBatches([]);
    setTrainingSubjects([]);
    setShowStudentList(false);
    resetPhotoStates();

    if (value) {
      fetchTrainingBatches(value);
      fetchTrainingSubjects(value);
    }
  };

  const handleTrainingBatchChange = (value) => {
    setSelectedTrainingBatch(value);
    setSelectedTrainingSubject('');
    setShowStudentList(false);
    resetPhotoStates();
  };

  const handleTrainingSubjectChange = (value) => {
    setSelectedTrainingSubject(value);
    resetPhotoStates();
    if (value && selectedTrainingBatch && selectedTrainingProgram) {
      fetchTrainingStudents(selectedTrainingBatch, selectedTrainingProgram);
    } else {
      setShowStudentList(false);
    }
  };

  const resetPhotoStates = () => {
    setCapturedPhoto(null);
    setPhotoUploaded(false);
    setShowCamera(false);
    setFaceCount(0);
    setCountMatched(false);
    setShowCountComparison(false);
    stopCamera();
  };

  // ============================================
  // ATTENDANCE FUNCTIONS
  // ============================================
  const toggleAttendanceStatus = (studentId) => {
    const student = students.find(s => s.student_id === studentId);

    if (student && student.is_in_internship) {
      const statusMessages = {
        'APPROVED': '✅ This student will be marked PRESENT (Internship Approved)',
        'REJECTED': '❌ This student will be marked ABSENT (Internship Rejected)',
        'PENDING': '⏳ This student will be marked PRESENT (Internship Pending)',
        'NO_SUBMISSION': '⚠️ This student will be marked ABSENT (No Internship Submission)'
      };

      const message = statusMessages[student.internship_attendance_today] ||
        '🔒 Cannot modify attendance for internship students';

      showNotification(message, 'warning');
      return;
    }

    setAttendanceData(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        status: prev[studentId].status === 'present' ? 'absent' : 'present',
        notificationStatus: 'pending'
      }
    }));
  };

  const getInternshipStatusDisplay = (student) => {
    if (!student.is_in_internship) {
      return {
        text: 'Regular',
        color: '#e8f5e9',
        textColor: '#1b5e20'
      };
    }

    switch (student.internship_attendance_today) {
      case 'APPROVED':
        return {
          text: '✅ APPROVED',
          color: '#e8f5e9',
          textColor: '#1b5e20'
        };
      case 'REJECTED':
        return {
          text: '❌ REJECTED',
          color: '#ffebee',
          textColor: '#c62828'
        };
      case 'PENDING':
        return {
          text: '⏳ PENDING',
          color: '#fff3e0',
          textColor: '#ef6c00'
        };
      case 'NO_SUBMISSION':
        return {
          text: '⚠️ NO SUBMISSION',
          color: '#fce4ec',
          textColor: '#ad1457'
        };
      default:
        return {
          text: '🏢 INTERNSHIP',
          color: '#fff8e1',
          textColor: '#ff6f00'
        };
    }
  };

  const markAllPresent = () => {
    const currentStudents = attendanceType === 'regular' ? students : trainingStudents;
    const updatedData = {};
    currentStudents.forEach(student => {
      updatedData[student.student_id] = {
        ...attendanceData[student.student_id],
        status: 'present',
        notificationStatus: 'pending'
      };
    });
    setAttendanceData(updatedData);
  };

  const markAllAbsent = () => {
    const currentStudents = attendanceType === 'regular' ? students : trainingStudents;
    const updatedData = {};
    let autoManagedCount = 0;

    currentStudents.forEach(student => {
      if (student.is_in_internship) {
        // Auto-manage based on internship status
        let autoStatus = 'present';
        if (student.internship_attendance_today === 'REJECTED' ||
          student.internship_attendance_today === 'NO_SUBMISSION') {
          autoStatus = 'absent';
        }

        updatedData[student.student_id] = {
          ...attendanceData[student.student_id],
          status: autoStatus,
          notificationStatus: 'pending'
        };
        autoManagedCount++;
      } else {
        updatedData[student.student_id] = {
          ...attendanceData[student.student_id],
          status: 'absent',
          notificationStatus: 'pending'
        };
      }
    });
    setAttendanceData(updatedData);

    if (autoManagedCount > 0) {
      showNotification(`${autoManagedCount} internship students auto-managed based on their internship status`, 'info');
    }
  };

  const getAttendanceStats = () => {
    const currentStudents = attendanceType === 'regular' ? students : trainingStudents;
    const total = currentStudents.length;
    const present = currentStudents.filter(student =>
      attendanceData[student.student_id]?.status === 'present'
    ).length;
    const absent = total - present;
    const internshipCount = currentStudents.filter(student => student.is_in_internship).length;

    return { total, present, absent, internshipCount };
  };

  // ============================================
  // SUBMIT FUNCTIONS
  // ============================================
  const submitAttendance = async () => {
    // Check if photo is uploaded
    if (!photoUploaded) {
      showNotification('Please capture and upload classroom photo before submitting attendance', 'error');
      return;
    }

    if (attendanceType === 'regular') {
      return submitRegularAttendance();
    } else {
      return submitTrainingAttendance();
    }
  };

  const submitRegularAttendance = async () => {
    if (!selectedDepartment || !selectedYear || !selectedBatch || !selectedSubject) {
      showNotification('Please select all required fields', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      const attendanceRecords = students.map(student => ({
        student_id: student.student_id,
        status: attendanceData[student.student_id]?.status || 'present'
      }));

      const requestData = {
        date: new Date().toISOString().split('T')[0],
        department: selectedDepartment,
        year: selectedYear,
        batch_name: selectedBatch,
        subject: selectedSubject,
        attendance_records: attendanceRecords
      };

      const response = await apiCall('/attendance/submit', {
        method: 'POST',
        body: JSON.stringify(requestData)
      });

      if (response.success) {
        showNotification('Successfully submitted', 'success');
      } else {
        showNotification('Error in submitting', 'error');
      }

    } catch (error) {
      console.error('Technical error:', error);
      showNotification('Error in submitting', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitTrainingAttendance = async () => {
    if (!selectedTrainingProgram || !selectedTrainingBatch || !selectedTrainingSubject) {
      showNotification('Please select all required fields', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      const attendanceRecords = trainingStudents.map(student => ({
        student_id: student.student_id,
        status: attendanceData[student.student_id]?.status || 'present'
      }));

      const selectedProgram = trainingPrograms.find(p => p.id == selectedTrainingProgram);
      const selectedBatch = trainingBatches.find(b => b.id == selectedTrainingBatch);
      const selectedSubjectObj = trainingSubjects.find(s => s.subject_code === selectedTrainingSubject);

      const requestData = {
        date: new Date().toISOString().split('T')[0],
        training_program_id: selectedTrainingProgram,
        training_batch_id: selectedTrainingBatch,
        subject_code: selectedTrainingSubject,
        subject_name: selectedSubjectObj?.subject_name || '',
        program_code: selectedProgram?.program_code || '',
        batch_code: selectedBatch?.batch_code || '',
        attendance_records: attendanceRecords
      };

      const response = await apiCall('/training/attendance/submit', {
        method: 'POST',
        body: JSON.stringify(requestData)
      });

      if (response.success) {
        showNotification('✅ Training attendance saved successfully with photo!', 'success');
      } else {
        throw new Error(response.message || 'Failed to save training attendance');
      }

    } catch (error) {
      console.error('💥 Error submitting training attendance:', error);
      showNotification('Error submitting training attendance: ' + error.message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================
  const showNotification = (message, type = 'info') => {
    const notification = {
      id: Date.now(),
      message,
      type
    };

    setNotifications(prev => [...prev, notification]);

    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 5000);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const getCurrentDate = () => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date().toLocaleDateString('en-US', options);
  };

  const stats = getAttendanceStats();
  const currentStudents = attendanceType === 'regular' ? students : trainingStudents;

  // Loading state
  if (loading && !currentStudents.length) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Attendance...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <Layout role="teacher" onNavigate={onNavigate} activePage="teacher-attendance" userInfo={userInfo}>
      {/* Page-specific CSS animations */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          @keyframes slideIn {
            from { transform: translateX(400px); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }

          @keyframes pulse {
            0% { opacity: 0.6; }
            50% { opacity: 1; }
            100% { opacity: 0.6; }
          }

          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
      {/* Page Header */}
      <div style={{
        padding: isMobile ? '15px 0 10px 0' : '25px 0 15px 0',
        marginBottom: isMobile ? '15px' : '25px',
        borderBottom: '1px solid #eee'
      }}>
        <h1 style={{
          margin: '0 0 10px 0',
          fontSize: isMobile ? '1.2rem' : '1.8rem',
          color: '#333',
          fontWeight: 600,
          display: 'flex',
          alignItems: isMobile ? 'flex-start' : 'center',
          flexWrap: 'wrap',
          gap: '0.5rem'
        }}>
          AI-Enhanced Attendance Management
          {faceApiLoaded && (
            <span style={{
              fontSize: '0.65rem',
              backgroundColor: '#e8f5e9',
              color: '#1b5e20',
              padding: '2px 8px',
              borderRadius: '12px',
              fontWeight: 'normal',
              whiteSpace: 'nowrap'
            }}>
              🤖 AI Ready
            </span>
          )}
        </h1>
        <p style={{
          color: '#666',
          fontSize: isMobile ? '0.8rem' : '0.9rem',
          margin: 0
        }}>
          {getCurrentDate()}
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div style={{
          background: '#ffebee',
          border: '1px solid #f5c6cb',
          color: '#721c24',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <span style={{ fontSize: '1.2rem' }}>⚠</span>
          {error}
        </div>
      )}

      {/* ATTENDANCE TYPE TOGGLE */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: isMobile ? '1rem' : '2rem',
        overflow: 'hidden'
      }}>
        <div style={{
          background: '#238548',
          color: '#ffffff',
          padding: isMobile ? '0.8rem 1rem' : '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '0.5rem' : '1rem'
        }}>
          <span style={{ fontSize: isMobile ? '1.3rem' : '1.8rem' }}>🎯</span>
          <h3 style={{ margin: 0, fontSize: isMobile ? '1rem' : '1.2rem' }}>Attendance Type</h3>
        </div>

        <div style={{ padding: isMobile ? '0.8rem' : '1.5rem' }}>
          <div style={{
            display: 'flex',
            gap: isMobile ? '0.5rem' : '1rem',
            flexDirection: isMobile ? 'column' : 'row',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => handleAttendanceTypeChange('regular')}
              style={{
                flex: 1,
                minWidth: isMobile ? 'unset' : '200px',
                padding: isMobile ? '0.7rem 1rem' : '1rem 1.5rem',
                border: `2px solid ${attendanceType === 'regular' ? '#238548' : '#ddd'}`,
                borderRadius: '8px',
                backgroundColor: attendanceType === 'regular' ? '#238548' : '#f9f9f9',
                color: attendanceType === 'regular' ? '#ffffff' : '#333',
                fontSize: isMobile ? '0.9rem' : '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <span style={{ fontSize: isMobile ? '1rem' : '1.2rem' }}>📚</span>
              Regular Classes
            </button>

            <button
              onClick={() => handleAttendanceTypeChange('training')}
              style={{
                flex: 1,
                minWidth: isMobile ? 'unset' : '200px',
                padding: isMobile ? '0.7rem 1rem' : '1rem 1.5rem',
                border: `2px solid ${attendanceType === 'training' ? '#ff6b35' : '#ddd'}`,
                borderRadius: '8px',
                backgroundColor: attendanceType === 'training' ? '#ff6b35' : '#f9f9f9',
                color: attendanceType === 'training' ? '#ffffff' : '#333',
                fontSize: isMobile ? '0.9rem' : '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <span style={{ fontSize: isMobile ? '1rem' : '1.2rem' }}>🚀</span>
              Training Programs
            </button>
          </div>
        </div>
      </div>

      {/* Take Attendance Section */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: isMobile ? '1rem' : '2rem',
        overflow: 'hidden'
      }}>
        <div style={{
          background: attendanceType === 'regular' ? '#238548' : '#ff6b35',
          color: '#ffffff',
          padding: isMobile ? '0.8rem 1rem' : '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '0.5rem' : '1rem'
        }}>
          <span style={{ fontSize: isMobile ? '1.3rem' : '1.8rem' }}>
            {attendanceType === 'regular' ? '✅' : '🎯'}
          </span>
          <h3 style={{ margin: 0, fontSize: isMobile ? '1rem' : '1.2rem' }}>
            Take {attendanceType === 'regular' ? 'Regular' : 'Training'} Attendance
          </h3>
          {isSubmitting && (
            <span style={{
              marginLeft: 'auto',
              fontSize: isMobile ? '0.75rem' : '0.9rem',
              opacity: 0.9,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <div style={{
                width: '14px',
                height: '14px',
                border: '2px solid rgba(255,255,255,0.3)',
                borderRadius: '50%',
                borderTop: '2px solid white',
                animation: 'spin 1s infinite linear'
              }}></div>
              Saving...
            </span>
          )}
        </div>

        <div style={{ padding: isMobile ? '0.6rem 0.5rem' : '1.5rem' }}>
          {/* Filter Section */}
          <div style={{
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            padding: isMobile ? '10px' : '15px',
            marginBottom: isMobile ? '12px' : '20px',
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: isMobile ? '10px' : '15px',
            boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
          }}>
            {attendanceType === 'regular' ? (
              <>
                {/* Regular Attendance Filters */}
                <div style={{ gridColumn: 'auto' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: isMobile ? '2px' : '5px',
                    fontWeight: '500',
                    color: '#444',
                    fontSize: isMobile ? '0.75rem' : '0.9rem'
                  }}>
                    Department:
                  </label>
                  <select
                    value={selectedDepartment}
                    onChange={(e) => handleDepartmentChange(e.target.value)}
                    style={{
                      width: '100%',
                      padding: isMobile ? '6px 8px' : '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: 'white',
                      transition: 'border-color 0.3s',
                      fontSize: isMobile ? '0.8rem' : '0.95rem',
                      height: isMobile ? '34px' : 'auto'
                    }}
                  >
                    <option value="">Select Department</option>
                    {departments.map(dept => (
                      <option key={dept.value} value={dept.value}>
                        {dept.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ gridColumn: 'auto' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: isMobile ? '2px' : '5px',
                    fontWeight: '500',
                    color: '#444',
                    fontSize: isMobile ? '0.75rem' : '0.9rem'
                  }}>
                    Year:
                  </label>
                  <select
                    value={selectedYear}
                    onChange={(e) => handleYearChange(e.target.value)}
                    disabled={!selectedDepartment || availableYears.length === 0}
                    style={{
                      width: '100%',
                      padding: isMobile ? '6px 8px' : '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: (selectedDepartment && availableYears.length > 0) ? 'white' : '#f5f5f5',
                      transition: 'border-color 0.3s',
                      cursor: (selectedDepartment && availableYears.length > 0) ? 'pointer' : 'not-allowed',
                      fontSize: isMobile ? '0.8rem' : '0.95rem',
                      height: isMobile ? '34px' : 'auto'
                    }}
                  >
                    <option value="">
                      {!selectedDepartment ? 'Select Department First' :
                        availableYears.length === 0 ? 'Loading Years...' : 'Select Year'}
                    </option>
                    {availableYears.map(year => (
                      <option key={year.value} value={year.value}>
                        {year.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ gridColumn: 'auto' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: isMobile ? '2px' : '5px',
                    fontWeight: '500',
                    color: '#444',
                    fontSize: isMobile ? '0.75rem' : '0.9rem'
                  }}>
                    Batch:
                  </label>
                  <select
                    value={selectedBatch}
                    onChange={(e) => handleBatchChange(e.target.value)}
                    disabled={!selectedYear || batches.length === 0}
                    style={{
                      width: '100%',
                      padding: isMobile ? '6px 8px' : '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: (selectedYear && batches.length > 0) ? 'white' : '#f5f5f5',
                      transition: 'border-color 0.3s',
                      cursor: (selectedYear && batches.length > 0) ? 'pointer' : 'not-allowed',
                      fontSize: isMobile ? '0.8rem' : '0.95rem',
                      height: isMobile ? '34px' : 'auto'
                    }}
                  >
                    <option value="">
                      {!selectedYear ? 'Select Year First' :
                        batches.length === 0 ? 'No Batches Available' : 'Select Batch'}
                    </option>
                    {batches.map(batch => (
                      <option key={batch} value={batch}>{batch}</option>
                    ))}
                  </select>
                </div>

                <div style={{ gridColumn: 'auto' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: isMobile ? '2px' : '5px',
                    fontWeight: '500',
                    color: '#444',
                    fontSize: isMobile ? '0.75rem' : '0.9rem'
                  }}>
                    Subject:
                  </label>
                  <select
                    value={selectedSubject}
                    onChange={(e) => handleSubjectChange(e.target.value)}
                    disabled={!selectedBatch || subjects.length === 0}
                    style={{
                      width: '100%',
                      padding: isMobile ? '6px 8px' : '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: (selectedBatch && subjects.length > 0) ? 'white' : '#f5f5f5',
                      transition: 'border-color 0.3s',
                      cursor: (selectedBatch && subjects.length > 0) ? 'pointer' : 'not-allowed',
                      fontSize: isMobile ? '0.8rem' : '0.95rem',
                      height: isMobile ? '34px' : 'auto'
                    }}
                  >
                    <option value="">
                      {!selectedBatch ? 'Select Batch First' :
                        subjects.length === 0 ? 'No Subjects Available' : 'Select Subject'}
                    </option>
                    {subjects.map(subject => (
                      <option key={subject.subject_code} value={subject.subject_code}>
                        {subject.subject_code} - {subject.subject_name}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            ) : (
              <>
                {/* Training Attendance Filters */}
                <div style={{ gridColumn: isMobile ? 'span 1' : 'auto' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: isMobile ? '2px' : '5px',
                    fontWeight: '500',
                    color: '#444',
                    fontSize: isMobile ? '0.75rem' : '0.9rem'
                  }}>
                    Training Program:
                  </label>
                  <select
                    value={selectedTrainingProgram}
                    onChange={(e) => handleTrainingProgramChange(e.target.value)}
                    style={{
                      width: '100%',
                      padding: isMobile ? '6px 8px' : '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: 'white',
                      transition: 'border-color 0.3s',
                      fontSize: isMobile ? '0.8rem' : '0.95rem',
                      height: isMobile ? '34px' : 'auto'
                    }}
                  >
                    <option value="">Select Training Program</option>
                    {trainingPrograms.map(program => (
                      <option key={program.id} value={program.id}>
                        {program.program_code} - {program.program_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ gridColumn: isMobile ? 'span 1' : 'auto' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: isMobile ? '2px' : '5px',
                    fontWeight: '500',
                    color: '#444',
                    fontSize: isMobile ? '0.75rem' : '0.9rem'
                  }}>
                    Training Batch:
                  </label>
                  <select
                    value={selectedTrainingBatch}
                    onChange={(e) => handleTrainingBatchChange(e.target.value)}
                    disabled={!selectedTrainingProgram}
                    style={{
                      width: '100%',
                      padding: isMobile ? '6px 8px' : '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: selectedTrainingProgram ? 'white' : '#f5f5f5',
                      transition: 'border-color 0.3s',
                      cursor: selectedTrainingProgram ? 'pointer' : 'not-allowed',
                      fontSize: isMobile ? '0.8rem' : '0.95rem',
                      height: isMobile ? '34px' : 'auto'
                    }}
                  >
                    <option value="">Select Training Batch</option>
                    {trainingBatches.map(batch => (
                      <option key={batch.id} value={batch.id}>
                        {batch.batch_name} ({batch.schedule_time})
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ gridColumn: isMobile ? 'span 2' : 'auto' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: isMobile ? '2px' : '5px',
                    fontWeight: '500',
                    color: '#444',
                    fontSize: isMobile ? '0.75rem' : '0.9rem'
                  }}>
                    Training Subject:
                  </label>
                  <select
                    value={selectedTrainingSubject}
                    onChange={(e) => handleTrainingSubjectChange(e.target.value)}
                    disabled={!selectedTrainingBatch}
                    style={{
                      width: '100%',
                      padding: isMobile ? '6px 8px' : '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: selectedTrainingBatch ? 'white' : '#f5f5f5',
                      transition: 'border-color 0.3s',
                      cursor: selectedTrainingBatch ? 'pointer' : 'not-allowed',
                      fontSize: isMobile ? '0.8rem' : '0.95rem',
                      height: isMobile ? '34px' : 'auto'
                    }}
                  >
                    <option value="">Select Training Subject</option>
                    {trainingSubjects.map(subject => (
                      <option key={subject.subject_code} value={subject.subject_code}>
                        {subject.subject_code} - {subject.subject_name}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>

          {/* AI-Enhanced Photo Capture Section */}
          {showStudentList && (
            <div style={{
              backgroundColor: '#f0f8ff',
              borderRadius: '8px',
              padding: isMobile ? '10px 8px' : '20px',
              marginBottom: isMobile ? '10px' : '20px',
              border: `1px solid ${photoUploaded ? '#4caf50' : '#2196f3'}`
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: isMobile ? '8px' : '15px',
                flexWrap: 'wrap',
                gap: '10px'
              }}>
                <h4 style={{
                  margin: 0,
                  fontSize: isMobile ? '0.85rem' : '1.1rem',
                  color: '#333',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  🤖 AI Classroom Photo {photoUploaded ? '✅' : '⚠️'}
                  <span style={{
                    fontSize: isMobile ? '0.7rem' : '0.8rem',
                    color: photoUploaded ? '#4caf50' : '#2196f3',
                    fontWeight: 'normal'
                  }}>
                    {photoUploaded ? '(Uploaded)' : '(AI head count required)'}
                  </span>
                </h4>

                {!photoUploaded && (
                  <button
                    onClick={startCamera}
                    disabled={showCamera}
                    style={{
                      backgroundColor: showCamera ? '#ccc' : '#2196f3',
                      color: 'white',
                      border: 'none',
                      padding: isMobile ? '6px 12px' : '10px 20px',
                      borderRadius: '4px',
                      fontSize: isMobile ? '0.75rem' : '0.9rem',
                      cursor: showCamera ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    📷 {showCamera ? 'Camera Active' : 'Take AI Photo'}
                  </button>
                )}

                {photoUploaded && (
                  <button
                    onClick={retakePhoto}
                    style={{
                      backgroundColor: '#ff9800',
                      color: 'white',
                      border: 'none',
                      padding: isMobile ? '6px 12px' : '10px 20px',
                      borderRadius: '4px',
                      fontSize: isMobile ? '0.75rem' : '0.9rem',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    🔄 Retake AI Photo
                  </button>
                )}
              </div>

              {/* AI Head Count Info */}
              {faceApiLoaded && (
                <div style={{
                  backgroundColor: '#e8f5e9',
                  padding: isMobile ? '6px 10px' : '10px 15px',
                  borderRadius: '6px',
                  marginBottom: isMobile ? '8px' : '15px',
                  border: '1px solid #4caf50'
                }}>
                  <p style={{
                    margin: 0,
                    fontSize: isMobile ? '0.75rem' : '0.9rem',
                    color: '#1b5e20',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    🤖 <strong>AI Head Counter Active:</strong> The system will automatically count faces in your photo and compare with students marked present.
                  </p>
                </div>
              )}

              {/* Camera View */}
              {showCamera && (
                <div style={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '500px',
                  margin: '0 auto',
                  backgroundColor: '#000',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  minHeight: isMobile ? '200px' : '300px'
                }}>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{
                      width: '100%',
                      height: 'auto',
                      minHeight: isMobile ? '200px' : '300px',
                      display: 'block',
                      objectFit: 'cover'
                    }}
                  />

                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: 'white',
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    padding: isMobile ? '8px 12px' : '10px 20px',
                    borderRadius: '6px',
                    fontSize: isMobile ? '0.8rem' : '0.9rem',
                    display: videoRef.current && videoRef.current.readyState >= 2 ? 'none' : 'block'
                  }}>
                    📷 Starting AI camera...
                  </div>

                  <div style={{
                    position: 'absolute',
                    bottom: isMobile ? '10px' : '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: isMobile ? '8px' : '15px'
                  }}>
                    <button
                      onClick={capturePhoto}
                      style={{
                        backgroundColor: '#4caf50',
                        color: 'white',
                        border: 'none',
                        padding: isMobile ? '8px 16px' : '12px 20px',
                        borderRadius: '50px',
                        fontSize: isMobile ? '0.85rem' : '1rem',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      🤖 AI Capture
                    </button>
                    <button
                      onClick={stopCamera}
                      style={{
                        backgroundColor: '#f44336',
                        color: 'white',
                        border: 'none',
                        padding: isMobile ? '8px 16px' : '12px 20px',
                        borderRadius: '50px',
                        fontSize: isMobile ? '0.85rem' : '1rem',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      ❌ Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* AI Analysis Display */}
              {analyzingPhoto && (
                <div style={{
                  textAlign: 'center',
                  padding: isMobile ? '12px' : '20px',
                  backgroundColor: '#fff3e0',
                  borderRadius: '6px',
                  border: '1px solid #ff9800',
                  marginTop: isMobile ? '8px' : '15px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    marginBottom: '10px'
                  }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: '2px solid #ff9800',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    <span style={{ color: '#e65100', fontWeight: '500', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                      🤖 AI is analyzing photo and counting faces...
                    </span>
                  </div>
                </div>
              )}

              {/* Head Count Comparison */}
              {showCountComparison && !analyzingPhoto && (
                <div style={{
                  backgroundColor: countMatched ? '#e8f5e9' : '#ffebee',
                  padding: isMobile ? '10px' : '15px',
                  borderRadius: '6px',
                  border: `1px solid ${countMatched ? '#4caf50' : '#f44336'}`,
                  marginTop: isMobile ? '8px' : '15px'
                }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
                    gap: isMobile ? '8px' : '15px',
                    marginBottom: '10px'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        fontSize: isMobile ? '1.5rem' : '2rem',
                        fontWeight: 'bold',
                        color: '#2196f3'
                      }}>
                        {faceCount}
                      </div>
                      <div style={{ fontSize: isMobile ? '0.75rem' : '0.9rem', color: '#666' }}>
                        Faces Detected
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        fontSize: isMobile ? '1.5rem' : '2rem',
                        fontWeight: 'bold',
                        color: '#4caf50'
                      }}>
                        {stats.present}
                      </div>
                      <div style={{ fontSize: isMobile ? '0.75rem' : '0.9rem', color: '#666' }}>
                        Students Present
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        fontSize: isMobile ? '1.2rem' : '1.5rem',
                        color: countMatched ? '#4caf50' : '#f44336'
                      }}>
                        {countMatched ? '✅' : '❌'}
                      </div>
                      <div style={{ fontSize: isMobile ? '0.75rem' : '0.9rem', color: '#666' }}>
                        Status
                      </div>
                    </div>
                  </div>
                  <div style={{
                    textAlign: 'center',
                    fontSize: isMobile ? '0.8rem' : '0.9rem',
                    color: countMatched ? '#1b5e20' : '#c62828',
                    fontWeight: '500'
                  }}>
                    {countMatched
                      ? '🎉 Perfect match! Head count verified.'
                      : '⚠️ Count mismatch detected. Please verify attendance or retake photo.'
                    }
                  </div>
                </div>
              )}

              {/* Consent Checkboxes */}
              {capturedPhoto && !photoUploaded && !analyzingPhoto && ((showCountComparison && !countMatched) || isImageBlurry) && (
                <div style={{
                  backgroundColor: '#fff3e0',
                  padding: isMobile ? '10px' : '15px',
                  borderRadius: '6px',
                  border: isMobile ? '1px solid #ff9800' : '2px solid #ff9800',
                  marginTop: isMobile ? '8px' : '15px'
                }}>
                  <div style={{
                    fontSize: isMobile ? '0.85rem' : '1rem',
                    fontWeight: '600',
                    color: '#e65100',
                    marginBottom: isMobile ? '8px' : '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    ⚠️ Consent Required
                  </div>

                  {showCountComparison && !countMatched && (
                    <label style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: isMobile ? '6px' : '10px',
                      marginBottom: isImageBlurry ? (isMobile ? '8px' : '12px') : '0',
                      cursor: 'pointer',
                      padding: isMobile ? '6px' : '10px',
                      backgroundColor: 'white',
                      borderRadius: '4px',
                      border: '1px solid #ffb74d'
                    }}>
                      <input
                        type="checkbox"
                        checked={consentChecked}
                        onChange={(e) => setConsentChecked(e.target.checked)}
                        style={{
                          width: isMobile ? '16px' : '18px',
                          height: isMobile ? '16px' : '18px',
                          marginTop: '2px',
                          cursor: 'pointer',
                          accentColor: '#ff9800'
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <span style={{
                          fontSize: isMobile ? '0.75rem' : '0.9rem',
                          color: '#333',
                          lineHeight: '1.4'
                        }}>
                          I acknowledge that the student count does not match the AI detection ({faceCount} faces detected vs {stats.present} students present).
                          <strong style={{ color: '#d32f2f', marginLeft: '3px' }}>In case of any discrepancy in attendance, there will be disciplinary actions.</strong>
                        </span>
                      </div>
                    </label>
                  )}

                  {isImageBlurry && (
                    <label style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: isMobile ? '6px' : '10px',
                      cursor: 'pointer',
                      padding: isMobile ? '6px' : '10px',
                      backgroundColor: 'white',
                      borderRadius: '4px',
                      border: '1px solid #ffb74d'
                    }}>
                      <input
                        type="checkbox"
                        checked={blurryConsentChecked}
                        onChange={(e) => setBlurryConsentChecked(e.target.checked)}
                        style={{
                          width: isMobile ? '16px' : '18px',
                          height: isMobile ? '16px' : '18px',
                          marginTop: '2px',
                          cursor: 'pointer',
                          accentColor: '#ff9800'
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <span style={{
                          fontSize: isMobile ? '0.75rem' : '0.9rem',
                          color: '#333',
                          lineHeight: '1.4'
                        }}>
                          I acknowledge that the captured image appears blurry.
                          <strong style={{ color: '#d32f2f', marginLeft: '3px' }}>In case of any discrepancy in attendance, there will be disciplinary actions.</strong>
                        </span>
                      </div>
                    </label>
                  )}
                </div>
              )}


              {/* Photo Preview and Upload */}
              {capturedPhoto && !photoUploaded && !analyzingPhoto && (
                <div style={{
                  textAlign: 'center',
                  marginTop: isMobile ? '8px' : '15px'
                }}>
                  <div style={{
                    display: 'inline-block',
                    maxWidth: isMobile ? '200px' : '300px',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    marginBottom: isMobile ? '8px' : '15px'
                  }}>
                    <img
                      src={capturedPhoto instanceof Blob ? URL.createObjectURL(capturedPhoto) : capturedPhoto}
                      alt="Captured classroom"
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button
                      onClick={uploadPhoto}
                      disabled={uploadingPhoto || (showCountComparison && !countMatched)}
                      style={{
                        backgroundColor:
                          uploadingPhoto ? '#ccc' :
                            (showCountComparison && !countMatched) ? '#ccc' : '#4caf50',
                        color: 'white',
                        border: 'none',
                        padding: isMobile ? '6px 12px' : '10px 20px',
                        borderRadius: '4px',
                        fontSize: isMobile ? '0.8rem' : '0.9rem',
                        cursor:
                          uploadingPhoto || (showCountComparison && !countMatched) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: isMobile ? '0.3rem' : '0.5rem'
                      }}
                    >
                      {uploadingPhoto ? (
                        <>
                          <div style={{
                            width: '14px',
                            height: '14px',
                            border: '2px solid rgba(255,255,255,0.3)',
                            borderRadius: '50%',
                            borderTop: '2px solid white',
                            animation: 'spin 1s infinite linear'
                          }} />
                          Uploading...
                        </>
                      ) : (
                        <>✅ Upload AI Photo</>
                      )}
                    </button>

                    {(showCountComparison && !countMatched || isImageBlurry) && (
                      <button
                        onClick={forceUpload}
                        disabled={uploadingPhoto || (showCountComparison && !countMatched && !consentChecked) || (isImageBlurry && !blurryConsentChecked)}
                        style={{
                          backgroundColor: (uploadingPhoto || (showCountComparison && !countMatched && !consentChecked) || (isImageBlurry && !blurryConsentChecked)) ? '#ccc' : '#ff9800',
                          color: 'white',
                          border: 'none',
                          padding: isMobile ? '6px 12px' : '10px 20px',
                          borderRadius: '4px',
                          fontSize: isMobile ? '0.8rem' : '0.9rem',
                          cursor: (uploadingPhoto || (showCountComparison && !countMatched && !consentChecked) || (isImageBlurry && !blurryConsentChecked)) ? 'not-allowed' : 'pointer',
                          transition: 'all 0.3s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: isMobile ? '0.3rem' : '0.5rem'
                        }}
                      >
                        🔓 Force Upload {(showCountComparison && !countMatched && !consentChecked) || (isImageBlurry && !blurryConsentChecked) ? '(Consent Required)' : ''}
                      </button>
                    )}

                    <button
                      onClick={retakePhoto}
                      disabled={uploadingPhoto}
                      style={{
                        backgroundColor: '#f44336',
                        color: 'white',
                        border: 'none',
                        padding: isMobile ? '6px 12px' : '10px 20px',
                        borderRadius: '4px',
                        fontSize: isMobile ? '0.8rem' : '0.9rem',
                        cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      🔄 Retake
                    </button>
                  </div>
                </div>
              )}

              {/* Photo Success Message */}
              {photoUploaded && capturedPhoto && (
                <div style={{
                  textAlign: 'center',
                  padding: isMobile ? '10px' : '15px',
                  backgroundColor: '#e8f5e9',
                  borderRadius: '6px',
                  border: '1px solid #4caf50',
                  marginTop: isMobile ? '8px' : '15px'
                }}>
                  <span style={{
                    color: '#1b5e20',
                    fontSize: isMobile ? '0.8rem' : '1rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}>
                    ✅ AI Photo uploaded!
                    {showCountComparison && (
                      <span style={{
                        fontSize: '0.8rem',
                        backgroundColor: 'rgba(27, 94, 32, 0.1)',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        marginLeft: '5px'
                      }}>
                        🤖 {faceCount} faces
                      </span>
                    )}
                  </span>
                </div>
              )}

              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </div>
          )}

          {/* Loading indicator for students */}
          {loading && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '2rem',
              gap: '1rem'
            }}>
              <div style={{
                width: '30px',
                height: '30px',
                border: '3px solid #e3f2fd',
                borderTop: `3px solid ${attendanceType === 'regular' ? '#238548' : '#ff6b35'}`,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <span>Loading {attendanceType === 'regular' ? 'students' : 'training participants'}...</span>
            </div>
          )}

          {/* Student List */}
          {showStudentList && !loading && (
            <div>
              {/* List Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: isMobile ? '10px' : '20px',
                paddingBottom: isMobile ? '6px' : '10px',
                borderBottom: '1px solid #eee',
                flexWrap: 'wrap',
                gap: '10px'
              }}>
                <h3 style={{
                  margin: 0,
                  color: '#333',
                  fontSize: isMobile ? '0.95rem' : '1.2rem'
                }}>
                  {attendanceType === 'regular' ?
                    `Student List - ${subjects.find(s => s.subject_code === selectedSubject)?.subject_name || selectedSubject}` :
                    `Training Participants - ${trainingSubjects.find(s => s.subject_code === selectedTrainingSubject)?.subject_name || selectedTrainingSubject}`
                  }
                </h3>
                <div style={{
                  display: 'flex',
                  gap: isMobile ? '6px' : '10px',
                  flexWrap: 'wrap'
                }}>
                  <button
                    onClick={markAllPresent}
                    style={{
                      backgroundColor: '#4caf50',
                      color: 'white',
                      border: 'none',
                      padding: isMobile ? '6px 10px' : '8px 15px',
                      borderRadius: '4px',
                      fontSize: isMobile ? '0.75rem' : '0.9rem',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: isMobile ? '3px' : '5px'
                    }}
                  >
                    ✓ Mark All Present
                  </button>
                  <button
                    onClick={markAllAbsent}
                    style={{
                      backgroundColor: '#f44336',
                      color: 'white',
                      border: 'none',
                      padding: isMobile ? '6px 10px' : '8px 15px',
                      borderRadius: '4px',
                      fontSize: isMobile ? '0.75rem' : '0.9rem',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: isMobile ? '3px' : '5px'
                    }}
                  >
                    ✕ Mark All Absent
                  </button>
                </div>
              </div>

              {/* Attendance Stats */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? `repeat(${3 + (stats.internshipCount > 0 ? 1 : 0) + (showCountComparison ? 1 : 0)}, 1fr)` : 'repeat(auto-fit, minmax(120px, 1fr))',
                backgroundColor: '#f5f7fa',
                borderRadius: '6px',
                padding: isMobile ? '8px 4px' : '15px',
                marginBottom: isMobile ? '12px' : '20px',
                gap: isMobile ? '6px' : '15px'
              }}>
                <div style={{
                  textAlign: 'center',
                  padding: isMobile ? '0 2px' : '0 15px'
                }}>
                  <span style={{
                    display: 'block',
                    fontSize: isMobile ? '0.65rem' : '0.9rem',
                    color: '#666',
                    marginBottom: isMobile ? '2px' : '5px'
                  }}>
                    Total:
                  </span>
                  <span style={{
                    display: 'block',
                    fontSize: isMobile ? '1.1rem' : '1.5rem',
                    fontWeight: '700',
                    color: '#333'
                  }}>
                    {stats.total}
                  </span>
                </div>
                <div style={{
                  textAlign: 'center',
                  padding: isMobile ? '0 2px' : '0 15px'
                }}>
                  <span style={{
                    display: 'block',
                    fontSize: isMobile ? '0.65rem' : '0.9rem',
                    color: '#666',
                    marginBottom: isMobile ? '2px' : '5px'
                  }}>
                    Present:
                  </span>
                  <span style={{
                    display: 'block',
                    fontSize: isMobile ? '1.1rem' : '1.5rem',
                    fontWeight: '700',
                    color: '#4caf50'
                  }}>
                    {stats.present}
                  </span>
                </div>
                <div style={{
                  textAlign: 'center',
                  padding: isMobile ? '0 2px' : '0 15px'
                }}>
                  <span style={{
                    display: 'block',
                    fontSize: isMobile ? '0.65rem' : '0.9rem',
                    color: '#666',
                    marginBottom: isMobile ? '2px' : '5px'
                  }}>
                    Absent:
                  </span>
                  <span style={{
                    display: 'block',
                    fontSize: isMobile ? '1.1rem' : '1.5rem',
                    fontWeight: '700',
                    color: '#f44336'
                  }}>
                    {stats.absent}
                  </span>
                </div>
                {stats.internshipCount > 0 && (
                  <div style={{
                    textAlign: 'center',
                    padding: isMobile ? '0 2px' : '0 15px'
                  }}>
                    <span style={{
                      display: 'block',
                      fontSize: isMobile ? '0.65rem' : '0.9rem',
                      color: '#666',
                      marginBottom: isMobile ? '2px' : '5px'
                    }}>
                      Internship:
                    </span>
                    <span style={{
                      display: 'block',
                      fontSize: isMobile ? '1.1rem' : '1.5rem',
                      fontWeight: '700',
                      color: '#ff9800'
                    }}>
                      {stats.internshipCount}
                    </span>
                  </div>
                )}
                {showCountComparison && (
                  <div style={{
                    textAlign: 'center',
                    padding: isMobile ? '0 2px' : '0 15px'
                  }}>
                    <span style={{
                      display: 'block',
                      fontSize: isMobile ? '0.65rem' : '0.9rem',
                      color: '#666',
                      marginBottom: isMobile ? '2px' : '5px'
                    }}>
                      AI Count:
                    </span>
                    <span style={{
                      display: 'block',
                      fontSize: isMobile ? '1.1rem' : '1.5rem',
                      fontWeight: '700',
                      color: countMatched ? '#4caf50' : '#f44336'
                    }}>
                      {faceCount} {countMatched ? '✅' : '❌'}
                    </span>
                  </div>
                )}
              </div>

              {/* Student Table */}
              <div style={{ overflowX: 'auto', margin: isMobile ? '0 -5px' : '0' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse'
                }}>
                  <thead>
                    <tr>
                      <th style={{
                        padding: isMobile ? '8px 4px' : '12px 15px',
                        textAlign: 'left',
                        borderBottom: '1px solid #eee',
                        backgroundColor: '#f9f9f9',
                        fontWeight: '600',
                        color: '#333',
                        fontSize: isMobile ? '0.75rem' : '0.9rem',
                        whiteSpace: 'nowrap'
                      }}>
                        Roll No.
                      </th>
                      <th style={{
                        padding: isMobile ? '8px 4px' : '12px 15px',
                        textAlign: 'left',
                        borderBottom: '1px solid #eee',
                        backgroundColor: '#f9f9f9',
                        fontWeight: '600',
                        color: '#333',
                        fontSize: isMobile ? '0.75rem' : '0.9rem',
                        whiteSpace: 'nowrap'
                      }}>
                        {attendanceType === 'regular' ? 'Student Name' : 'Participant Name'}
                      </th>
                      {attendanceType === 'training' && (
                        <th style={{
                          padding: isMobile ? '8px 4px' : '12px 15px',
                          textAlign: 'left',
                          borderBottom: '1px solid #eee',
                          backgroundColor: '#f9f9f9',
                          fontWeight: '600',
                          color: '#333',
                          fontSize: isMobile ? '0.75rem' : '0.9rem',
                          whiteSpace: 'nowrap'
                        }}>
                          Regular Batch
                        </th>
                      )}
                      <th style={{
                        padding: isMobile ? '8px 4px' : '12px 15px',
                        textAlign: 'center',
                        borderBottom: '1px solid #eee',
                        backgroundColor: '#f9f9f9',
                        fontWeight: '600',
                        color: '#333',
                        fontSize: isMobile ? '0.75rem' : '0.9rem',
                        whiteSpace: 'nowrap'
                      }}>
                        Status
                      </th>
                      <th style={{
                        padding: isMobile ? '8px 4px' : '12px 15px',
                        textAlign: 'left',
                        borderBottom: '1px solid #eee',
                        backgroundColor: '#f9f9f9',
                        fontWeight: '600',
                        color: '#333',
                        fontSize: isMobile ? '0.75rem' : '0.9rem',
                        whiteSpace: 'nowrap'
                      }}>
                        Parent's Contact
                      </th>
                      <th style={{
                        padding: isMobile ? '8px 4px' : '12px 15px',
                        textAlign: 'center',
                        borderBottom: '1px solid #eee',
                        backgroundColor: '#f9f9f9',
                        fontWeight: '600',
                        color: '#333',
                        fontSize: isMobile ? '0.75rem' : '0.9rem',
                        whiteSpace: 'nowrap'
                      }}>
                        Additional Info
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentStudents.map(student => {
                      const studentAttendance = attendanceData[student.student_id] || {
                        status: 'present',
                        notificationStatus: 'pending',
                        isInternship: !!student.is_in_internship
                      };

                      const isInternship = !!student.is_in_internship;
                      const statusDisplay = getInternshipStatusDisplay(student);

                      // Determine what the final attendance will be
                      let predictedStatus = studentAttendance.status;
                      let statusExplanation = '';

                      if (isInternship) {
                        if (student.internship_attendance_today === 'APPROVED') {
                          predictedStatus = 'present';
                          statusExplanation = 'Auto-Present (Internship Approved)';
                        } else if (student.internship_attendance_today === 'REJECTED') {
                          predictedStatus = 'absent';
                          statusExplanation = 'Auto-Absent (Internship Rejected)';
                        } else if (student.internship_attendance_today === 'PENDING') {
                          predictedStatus = 'present';
                          statusExplanation = 'Auto-Present (Internship Pending)';
                        } else {
                          predictedStatus = 'absent';
                          statusExplanation = 'Auto-Absent (No Internship Submission)';
                        }
                      }

                      return (
                        <tr key={student.student_id} style={{
                          borderBottom: '1px solid #eee',
                          backgroundColor: isInternship ? '#fff8e1' : 'transparent',
                          transition: 'background-color 0.3s'
                        }}>
                          <td style={{ 
                            padding: isMobile ? '8px 4px' : '12px 15px', 
                            fontSize: isMobile ? '0.72rem' : 'inherit',
                            whiteSpace: 'nowrap',
                            textAlign: 'left'
                          }}>
                            {student.roll_number}
                          </td>
                          <td style={{ 
                            padding: isMobile ? '8px 4px' : '12px 15px',
                            textAlign: 'left'
                          }}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span style={{ 
                                fontWeight: isInternship ? '600' : 'normal',
                                fontSize: isMobile ? '0.75rem' : 'inherit'
                              }}>
                                {attendanceType === 'regular' ? student.name : (student.actual_name || student.student_name)}
                              </span>
                              {isInternship && (
                                <span style={{
                                  fontSize: isMobile ? '0.65rem' : '0.75rem',
                                  color: '#ff6f00',
                                  fontWeight: '500',
                                  marginTop: '2px'
                                }}>
                                  🏢 {student.company_name}
                                </span>
                              )}
                              {isInternship && statusExplanation && (
                                <span style={{
                                  fontSize: isMobile ? '0.65rem' : '0.7rem',
                                  color: predictedStatus === 'present' ? '#4caf50' : '#f44336',
                                  fontStyle: 'italic',
                                  marginTop: '2px'
                                }}>
                                  {statusExplanation}
                                </span>
                              )}
                            </div>
                          </td>
                          {attendanceType === 'training' && (
                            <td style={{ 
                              padding: isMobile ? '8px 4px' : '12px 15px',
                              textAlign: 'left'
                            }}>
                              <span style={{
                                fontSize: isMobile ? '0.65rem' : '0.8rem',
                                backgroundColor: '#e3f2fd',
                                color: '#1976d2',
                                padding: isMobile ? '1px 4px' : '2px 6px',
                                borderRadius: '3px'
                              }}>
                                {student.regular_batch || student.student_batch}
                              </span>
                            </td>
                          )}
                          <td style={{ padding: isMobile ? '8px 4px' : '12px 15px', textAlign: 'center' }}>
                            <label style={{
                              position: 'relative',
                              display: 'inline-block',
                              width: isMobile ? '70px' : '100px',
                              height: isMobile ? '24px' : '34px',
                              cursor: isInternship ? 'not-allowed' : 'pointer'
                            }}>
                              <input
                                type="checkbox"
                                checked={predictedStatus === 'present'}
                                onChange={() => toggleAttendanceStatus(student.student_id)}
                                disabled={isInternship}
                                style={{
                                  opacity: 0,
                                  width: 0,
                                  height: 0
                                }}
                              />
                              <span style={{
                                position: 'absolute',
                                cursor: isInternship ? 'not-allowed' : 'pointer',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: isInternship ?
                                  (predictedStatus === 'present' ? '#4caf50' : '#f44336') :
                                  (predictedStatus === 'present' ? '#4caf50' : '#f44336'),
                                transition: '.4s',
                                borderRadius: isMobile ? '24px' : '34px',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: predictedStatus === 'present' ? 'flex-end' : 'flex-start',
                                paddingLeft: predictedStatus === 'present' ? (isMobile ? '6px' : '10px') : (isMobile ? '20px' : '35px'),
                                paddingRight: predictedStatus === 'present' ? (isMobile ? '20px' : '35px') : (isMobile ? '6px' : '10px'),
                                fontSize: isMobile ? '0.65rem' : '0.8rem',
                                fontWeight: '500',
                                opacity: isInternship ? 0.8 : 1
                              }}>
                                {isInternship ?
                                  (predictedStatus === 'present' ? (isMobile ? 'Auto-P' : 'Auto-Present') : (isMobile ? 'Auto-A' : 'Auto-Absent')) :
                                  (predictedStatus === 'present' ? 'Present' : 'Absent')
                                }
                                <span style={{
                                  position: 'absolute',
                                  height: isMobile ? '18px' : '26px',
                                  width: isMobile ? '18px' : '26px',
                                  left: predictedStatus === 'present' ? (isMobile ? '49px' : '69px') : (isMobile ? '3px' : '4px'),
                                  bottom: isMobile ? '3px' : '4px',
                                  backgroundColor: 'white',
                                  transition: '.4s',
                                  borderRadius: '50%'
                                }} />
                              </span>
                            </label>
                          </td>
                          <td style={{ 
                            padding: isMobile ? '8px 4px' : '12px 15px',
                            fontSize: isMobile ? '0.72rem' : 'inherit',
                            whiteSpace: 'nowrap',
                            textAlign: 'left'
                          }}>
                            {student.parents_number || 'N/A'}
                          </td>
                          <td style={{ padding: isMobile ? '8px 4px' : '12px 15px', textAlign: 'center' }}>
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px'
                            }}>
                              <span style={{
                                fontSize: isMobile ? '0.65rem' : '0.75rem',
                                backgroundColor: statusDisplay.color,
                                color: statusDisplay.textColor,
                                padding: isMobile ? '1px 4px' : '2px 6px',
                                borderRadius: '3px',
                                fontWeight: '500',
                                whiteSpace: 'nowrap'
                              }}>
                                {statusDisplay.text}
                              </span>
                              {isInternship && student.internship_end_date && (
                                <span style={{
                                  fontSize: isMobile ? '0.62rem' : '0.7rem',
                                  color: '#666',
                                  whiteSpace: 'nowrap'
                                }}>
                                  Till: {new Date(student.internship_end_date).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Submit Button */}
              <div style={{
                marginTop: isMobile ? '10px' : '20px',
                textAlign: 'center'
              }}>
                <button
                  onClick={submitAttendance}
                  disabled={isSubmitting || !photoUploaded}
                  style={{
                    backgroundColor:
                      isSubmitting ? '#78a9e6' :
                        !photoUploaded ? '#ccc' :
                          (attendanceType === 'regular' ? '#238548' : '#ff6b35'),
                    color: 'white',
                    border: 'none',
                    padding: isMobile ? '8px 16px' : '12px 25px',
                    borderRadius: '4px',
                    fontSize: isMobile ? '0.85rem' : '1rem',
                    fontWeight: '500',
                    cursor: (!photoUploaded || isSubmitting) ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.3s, transform 0.2s',
                    minWidth: isMobile ? '100%' : '300px',
                    boxSizing: 'border-box',
                    position: 'relative'
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <span style={{
                        width: '14px',
                        height: '14px',
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderRadius: '50%',
                        borderTop: '2px solid white',
                        animation: 'spin 1s infinite linear',
                        display: 'inline-block',
                        marginRight: '8px'
                      }} />
                      Saving {attendanceType === 'regular' ? 'Regular' : 'Training'} Attendance...
                    </>
                  ) : !photoUploaded ? (
                    <>
                      📸 Take AI Photo First to Submit Attendance
                    </>
                  ) : (
                    <>
                      🚀 Submit {attendanceType === 'regular' ? 'Regular' : 'Training'} Attendance
                    </>
                  )}
                </button>

                {!photoUploaded && (
                  <p style={{
                    marginTop: isMobile ? '6px' : '10px',
                    fontSize: isMobile ? '0.75rem' : '0.9rem',
                    color: '#ff6b35',
                    fontStyle: 'italic',
                    marginRight: 'auto',
                    marginLeft: 'auto',
                    maxWidth: '400px'
                  }}>
                    🤖 Please capture and upload AI-verified classroom photo before submitting attendance
                  </p>
                )}

                {photoUploaded && showCountComparison && countMatched && (
                  <p style={{
                    marginTop: isMobile ? '6px' : '10px',
                    fontSize: isMobile ? '0.75rem' : '0.9rem',
                    color: '#4caf50',
                    fontStyle: 'italic'
                  }}>
                    ✅ AI head count verified ({faceCount} faces detected, {stats.present} students present)
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Notifications */}
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 9999
      }}>
        {notifications.map(notification => (
          <div
            key={notification.id}
            style={{
              maxWidth: '350px',
              padding: '0',
              borderRadius: '4px',
              boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
              marginBottom: '10px',
              overflow: 'hidden',
              animation: 'slideIn 0.3s ease-out forwards',
              backgroundColor:
                notification.type === 'success' ? '#e8f5e9' :
                  notification.type === 'warning' ? '#fff3e0' :
                    notification.type === 'error' ? '#ffebee' : '#e3f2fd',
              borderLeft: `4px solid ${notification.type === 'success' ? '#4caf50' :
                notification.type === 'warning' ? '#ff9800' :
                  notification.type === 'error' ? '#f44336' : '#2196f3'
                }`
            }}
          >
            <div style={{
              padding: '15px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span style={{
                color:
                  notification.type === 'success' ? '#1b5e20' :
                    notification.type === 'warning' ? '#e65100' :
                      notification.type === 'error' ? '#b71c1c' : '#0d47a1'
              }}>
                {notification.message}
              </span>
              <button
                onClick={() => removeNotification(notification.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#666',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  marginLeft: '10px',
                  padding: 0
                }}
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>
    </Layout>
  );
};

export default TeacherAttendance;